import axios from 'axios';
import type { WorkerConversation, WorkerMessage } from '../types/platform';

// Worker data API — reads conversation/message/lead data from the chatbot worker's SQLite.
//
// ┌─────────────────────────────────────────────────────────────────────────────────┐
// │  PRODUCTION (CF Pages):                                                         │
// │    VITE_WORKER_URL is NOT set → WORKER_BASE_URL = '' (empty string)             │
// │    All calls go relative: /api/v1/db/conversations, /api/v1/stats, etc.         │
// │    CF Pages functions/_middleware.ts proxies /api/v1/* → svc-business-chatbot   │
// │    svc-business-chatbot then JWT-validates and forwards to the worker pod.       │
// │                                                                                 │
// │  LOCAL DEVELOPMENT:                                                             │
// │    Set VITE_WORKER_URL=http://localhost:8001 in .env.local                      │
// │    Calls go directly to wkr-chatbot (no JWT validation — for testing only).     │
// └─────────────────────────────────────────────────────────────────────────────────┘
const WORKER_BASE_URL: string =
  // If env var is defined and non-empty, use it (local dev / custom override).
  // Otherwise fall back to '' so calls are relative to the current origin (production).
  import.meta.env.VITE_WORKER_URL || '';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface TrendPoint {
  period: string;
  total: number;
}

export interface ScenarioStat {
  scenario_id: string;
  scenario_name: string;
  total: number;
}

export interface DashboardStats {
  customer_count: number;
  lead_count: number;
  conversation_count: number;
  message_today_count: number;
  customer_trend: TrendPoint[];
  lead_trend: TrendPoint[];
  leads_by_scenario: ScenarioStat[];
  group_by: string;
}

export interface MessageStats {
  channel_id: string;
  daily:   Record<string, number>;
  monthly: Record<string, number>;
  yearly:  Record<string, number>;
}

export interface WorkerUser {
  id: string;
  platform_user_id: string;
  platform: string;
  channel_id: string;
  workspace_id: string;
  name: string;
  avatar: string;
  locale: string;
  gender: string;
  timezone: number;
  first_seen_at: number;
  updated_at: number;
}

export interface SlotLead {
  id: string;
  conversation_id: string;
  scenario_id: string;
  scenario_name: string;
  user_id: string;
  user_name: string;
  user_avatar: string;
  platform: string;
  channel_id: string;
  collected: Record<string, string>;
  completed_at: number;
}

export interface PriorityInboxItem {
  id: string;
  platform: string;
  channel_id: string;
  user_id: string;
  customer_name: string;
  customer_avatar: string;
  locale: string;
  gender: string;
  last_message: string;
  last_message_at: number;
  unread_count: number;
  is_bot_handled: number;
  status: string;
  updated_at: number;
  // AI Intel
  interest_level: string;
  priority: number;           // 1-5
  what_they_want: string;
  info_provided: string;
  next_action: string;
  summary: string;
  interest_updated_at: number;
  // Slot leads
  leads: SlotLead[];
}

// ── Service ───────────────────────────────────────────────────────────────────

class WorkerApiService {
  private client: ReturnType<typeof axios.create>;

  constructor() {
    this.client = axios.create({
      baseURL: WORKER_BASE_URL,
      timeout: 10000,
      headers: { 'Content-Type': 'application/json' },
    });

    // Attach auth token to every request:
    // - Proxy mode: use session_token (standard user JWT via svc-business-chatbot)
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('session_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      const businessId = localStorage.getItem('oauth_business_id');
      if (businessId) {
        config.headers['X-Business-ID'] = businessId;
      }
      
      return config;
    });

    this.client.interceptors.response.use(
      (r) => r.data,
      (err) => Promise.reject(err),
    );
  }

  // ── Deprecated Direct Mode (No-op now) ──────────────────────────────────────
  setDirectMode(workerUrl: string, jwtToken: string) {
    // No-op: we now rely entirely on the proxy for all endpoints
  }

  clearDirectMode() {
    // No-op
  }

  async getConversations(params?: {
    channel_id?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ conversations: WorkerConversation[]; limit: number; offset: number }> {
    return this.client.get('/api/v1/db/conversations', { params });
  }

  async getMessages(
    conversationId: string,
    params?: { limit?: number; before_timestamp?: number },
  ): Promise<{ messages: WorkerMessage[]; conversation_id: string; count: number }> {
    return this.client.get(`/api/v1/db/conversations/${conversationId}/messages`, { params });
  }

  async markConversationRead(conversationId: string): Promise<void> {
    await this.client.post(`/api/v1/db/conversations/${conversationId}/read`);
  }

  // Bot config — source of truth is the worker's in-memory store
  // pageId = Facebook Page ID (platform.platform_id)
  async getBotConfig(pageId: string): Promise<any> {
    return this.client.get(`/api/v1/bot-config/${pageId}`);
  }

  /**
   * Partial update — send only the fields you want to change.
   * e.g. { bot_enabled: false } or { chatbot_mode: 'expert' }
   */
  async patchBotConfig(pageId: string, changes: Record<string, any>): Promise<any> {
    return this.client.patch(`/api/v1/bot-config/${pageId}`, changes);
  }

  // ── Follow-up trigger ─────────────────────────────────────────────────────

  /** Trigger followup scan according to bot_config (priority filter, max_attempts), bỏ qua điều kiện thời gian. */
  async triggerFollowupScan(channelId?: string): Promise<{
    success: boolean;
    stats: { channels_scanned: number; candidates: number; sent: number };
  }> {
    return this.client.post('/api/v1/followup/trigger', null, {
      params: channelId ? { channel_id: channelId } : undefined,
    });
  }

  /** Trigger followup cho danh sách conversation cụ thể (do user chọn từ UI). */
  async triggerFollowupBatch(
    channelId: string,
    conversationIds: string[],
    message?: string,
  ): Promise<{
    success: boolean;
    sent: string[];
    failed: { id: string; reason: string }[];
  }> {
    return this.client.post('/api/v1/followup/trigger-batch', {
      channel_id: channelId,
      conversation_ids: conversationIds,
      ...(message ? { message } : {}),
    });
  }

  // ── Dashboard ─────────────────────────────────────────────────────────────

  async getStats(params?: {
    channel_id?: string;
    group_by?: 'day' | 'month' | 'year';
  }): Promise<DashboardStats> {
    return this.client.get('/api/v1/stats', { params });
  }

  async getInterestStats(channel_id?: string): Promise<{ interest_stats: { label: string; count: number }[]; total: number }> {
    return this.client.get('/api/v1/stats/interest', { params: channel_id ? { channel_id } : undefined });
  }

  async getMessageStats(channel_id?: string): Promise<MessageStats> {
    return this.client.get('/api/v1/stats/messages', { params: channel_id ? { channel_id } : undefined });
  }

  async getPriorityInbox(params?: {
    channel_id?: string;
    min_priority?: number;
    limit?: number;
  }): Promise<{ items: PriorityInboxItem[]; total: number }> {
    return this.client.get('/api/v1/priority-inbox', { params });
  }

  async getCustomers(params?: {
    channel_id?: string;
    platform?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ users: WorkerUser[]; total: number; limit: number; offset: number }> {
    return this.client.get('/api/v1/db/users', { params });
  }

  async getLeads(params?: {
    channel_id?: string;
    scenario_id?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ leads: SlotLead[]; count: number; limit: number; offset: number }> {
    return this.client.get('/api/v1/slot-leads', { params });
  }

  async testChat(
    channelId: string,
    message: string,
  ): Promise<{ reply: string; media?: any[] }> {
    return this.client.post('/api/v1/test-chat', { channel_id: channelId, message });
  }

  async getTestChatHistory(
    channelId: string,
  ): Promise<{ channel_id: string; messages: { id: string; role: 'user' | 'assistant'; content: string; content_type?: string; metadata?: any; ts: number }[] }> {
    return this.client.get(`/api/v1/test-chat/history/${channelId}`);
  }

  async clearTestChatHistory(channelId: string): Promise<void> {
    return this.client.delete(`/api/v1/test-chat/history/${channelId}`);
  }

  // ── Polling Control (browser accounts) ────────────────────────────────────

  async startPolling(accountId: string): Promise<{ success: boolean; polling: boolean }> {
    return this.client.post(`/api/v1/polling/${accountId}/start`);
  }

  async stopPolling(accountId: string): Promise<{ success: boolean; polling: boolean }> {
    return this.client.post(`/api/v1/polling/${accountId}/stop`);
  }

  // ── Agent Actions ─────────────────────────────────────────────────────────

  async sendAgentMessage(params: {
    channel_id: string;
    user_id: string;
    message_text: string;
    conversation_id?: string;
  }): Promise<{ success: boolean }> {
    return this.client.post('/api/v1/agent-send', params);
  }

  async sendAgentImage(params: {
    channel_id: string;
    user_id: string;
    conversation_id: string;
    file: File;
  }): Promise<{ success: boolean; message_id?: string }> {
    const form = new FormData();
    form.append('channel_id', params.channel_id);
    form.append('user_id', params.user_id);
    form.append('conversation_id', params.conversation_id);
    form.append('file', params.file);
    return this.client.post('/api/v1/agent-send-image', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 30000,
    });
  }

  async setAgentControl(params: {
    conversation_id: string;
    channel_id: string;
    agent_controlled: boolean;
  }): Promise<{ success: boolean }> {
    return this.client.post('/api/v1/agent-control', params);
  }

  // ── Onboarding ────────────────────────────────────────────────────────────

  async processOnboarding(pageId: string, answers: Record<string, string>): Promise<{ success: boolean }> {
    return this.client.post(`/api/v1/onboarding/${pageId}`, { answers });
  }

  async interviewOnboarding(
    message: string,
    history: { role: string; content: string }[],
    section: 'business_info' | 'products' | 'bot_config',
  ): Promise<{
    done: boolean;
    reply: string;
    section_data?: Record<string, any>;
    items?: any[];
    products?: any[];
  }> {
    return this.client.post('/api/v1/onboarding/interview', { message, history, section });
  }

  // ── Voice-to-text ─────────────────────────────────────────────────────

  async transcribeVoice(audioBlob: Blob): Promise<{ text: string; language: string }> {
    const ext = audioBlob.type.includes('mp3') ? 'mp3' : audioBlob.type.includes('wav') ? 'wav' : 'webm';
    const form = new FormData();
    form.append('file', audioBlob, `recording.${ext}`);
    return this.client.post('/api/v1/voice/transcribe', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  }

  // ── Text-to-speech (Edge TTS) ─────────────────────────────────────────

  async getTTSVoices(): Promise<{ voices: { id: string; name: string; lang: string }[] }> {
    return this.client.get('/api/v1/voice/tts-voices');
  }

  async textToSpeech(text: string, voice: string = 'vi-VN-HoaiMyNeural'): Promise<Blob> {
    const res = await this.client.post('/api/v1/voice/tts', { text, voice }, { responseType: 'blob' });
    return res as unknown as Blob;
  }

  // ── Platforms (worker DB — channels collection) ──────────────────────

  async getPlatforms(): Promise<{ data: any[] }> {
    // Worker endpoint — returns all channels from worker's UnQLite DB.
    // After setDirectMode(), this goes directly to the worker tunnel.
    const res = await this.client.get('/api/v1/platforms');
    return { data: Array.isArray(res) ? res : (res as any).data ?? [] };
  }

  async getPlatform(platformId: string): Promise<{ data: any }> {
    // Worker endpoint — returns a single channel by ID.
    const res = await this.client.get(`/api/v1/platforms/${platformId}`);
    return { data: (res as any) };
  }

  async deletePlatform(platformId: string): Promise<{ success: boolean; deleted: Record<string, number | string> }> {
    return this.client.delete(`/api/v1/platforms/${platformId}`);
  }

  // ── Knowledge Base ────────────────────────────────────────────────────────

  async getKnowledgeBaseItems(channelId?: string, q?: string): Promise<{ data: any[]; total: number }> {
    return this.client.get('/api/v1/knowledge-base', {
      params: { ...(channelId ? { channel_id: channelId } : {}), ...(q ? { q } : {}) },
    });
  }

  async searchKnowledgeBase(query: string, channelId?: string): Promise<{ data: any[]; total: number }> {
    return this.client.get('/api/v1/knowledge-base/search', {
      params: { q: query, ...(channelId ? { channel_id: channelId } : {}) },
    });
  }

  async createKnowledgeBaseItem(data: any, channelId?: string): Promise<{ success: boolean; data: any }> {
    return this.client.post('/api/v1/knowledge-base', { ...data, ...(channelId ? { channel_id: channelId } : {}) });
  }

  async updateKnowledgeBaseItem(id: string, data: any): Promise<{ success: boolean; data: any }> {
    return this.client.put(`/api/v1/knowledge-base/${id}`, data);
  }

  async deleteKnowledgeBaseItem(id: string): Promise<{ success: boolean }> {
    return this.client.delete(`/api/v1/knowledge-base/${id}`);
  }

  async bulkImportKB(items: any[], channelId?: string): Promise<{ success: boolean; created: number; data: any[] }> {
    return this.client.post('/api/v1/knowledge-base/bulk-import', {
      items,
      ...(channelId ? { channel_id: channelId } : {}),
    });
  }

  async importKbPdf(params: {
    file: File;
    channelId: string;
    category?: string;
    extractImages?: boolean;
    maxImages?: number;
  }): Promise<{
    success: boolean;
    source_file: string;
    entries_created: number;
    text_chunks: number;
    image_entries: number;
    channel_id: string;
    message: string;
  }> {
    const form = new FormData();
    form.append('file', params.file);
    form.append('channel_id', params.channelId);
    if (params.category) form.append('category', params.category);
    form.append('extract_images', params.extractImages === false ? 'false' : 'true');
    if (params.maxImages != null) form.append('max_images', String(params.maxImages));
    return this.client.post('/api/v1/knowledge-base/import-pdf', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 1800000,  // 30 min — large PDFs with many images can be slow
    });
  }

  async estimatePdfCost(params: {
    file: File;
    extractImages?: boolean;
  }): Promise<{
    success: boolean;
    estimate: {
      filename: string;
      page_count: number;
      text_chunks: number;
      image_count: number;
      vision_calls: number;
      embed_calls: number;
      vision_input_tokens: number;
      vision_output_tokens: number;
      embed_tokens: number;
      vision_input_cost_usd: number;
      vision_output_cost_usd: number;
      embed_cost_usd: number;
      total_cost_usd: number;
      total_cost_vnd: number;
      usd_to_vnd_rate: number;
      estimated_seconds: number;
      extract_images: boolean;
      pricing: {
        vision_model: string;
        vision_input_per_M_usd: number;
        vision_output_per_M_usd: number;
        embed_model: string;
        embed_per_M_usd: number;
      };
      note: string;
    };
  }> {
    const form = new FormData();
    form.append('file', params.file);
    form.append('extract_images', params.extractImages === false ? 'false' : 'true');
    return this.client.post('/api/v1/knowledge-base/estimate-pdf', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    });
  }

  async deleteKbBySource(channelId: string, sourceFile: string): Promise<{ success: boolean; deleted: number; source_file: string }> {
    return this.client.delete('/api/v1/knowledge-base/by-source', {
      params: { channel_id: channelId, source_file: sourceFile },
    });
  }

  async bulkDeleteKB(ids: string[]): Promise<{ success: boolean; deleted: number }> {
    return this.client.delete('/api/v1/knowledge-base/bulk-delete', { data: { ids } });
  }

  async getPendingKBItems(channelId?: string, limit = 50, offset = 0): Promise<{ data: any[]; total: number }> {
    return this.client.get('/api/v1/knowledge-base/pending', {
      params: { ...(channelId ? { channel_id: channelId } : {}), limit, offset },
    });
  }

  async reviewKBItem(id: string, action: 'approve' | 'reject'): Promise<{ success: boolean; action: string; id: string }> {
    return this.client.post(`/api/v1/knowledge-base/${id}/review`, { action });
  }

  async extractKBFromText(text: string, channelId?: string): Promise<{ success: boolean; data: any[]; count: number }> {
    return this.client.post('/api/v1/knowledge-base/extract-ai', {
      text,
      ...(channelId ? { channel_id: channelId } : {}),
    }, { timeout: 120000 }); // 2 min — Gemini extraction can be slow for large texts
  }

  async interviewKB(
    message: string,
    history: { role: string; content: string }[],
  ): Promise<{ done: boolean; reply: string; items?: any[] }> {
    return this.client.post('/api/v1/knowledge-base/interview', { message, history });
  }

  // ── Zalo QR Login ──────────────────────────────────────────────────────────
  // These use this.client so they respect setDirectMode (worker direct URL).
  // Must call workerApiService.setDirectMode(url, jwt) via apiService.getWorkerURL()
  // before polling — otherwise in prod WORKER_BASE_URL='' routes to CF Pages (404).

  async zaloGetQRCode(accountId: string): Promise<{ status: string; qr_base64: string | null; message?: string }> {
    return this.client.get(`/api/v1/zalo/${accountId}/qr`);
  }

  async zaloGetLoginStatus(accountId: string): Promise<{ account_id: string; logged_in: boolean; status: string }> {
    return this.client.get(`/api/v1/zalo/${accountId}/login-status`);
  }

  async zaloCancelLogin(accountId: string): Promise<void> {
    await this.client.post(`/api/v1/zalo/${accountId}/cancel-login`);
  }
}

export const workerApi = new WorkerApiService();
// Alias for ChatbotDetail to call setDirectMode / clearDirectMode
export const workerApiService = workerApi;

// ── Export / Import (Backup) ────────────────────────────────────────────────────

function getAuthHeaders(): Record<string, string> {
  const token = localStorage.getItem('session_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function exportPageData(pageId: string): Promise<Record<string, any>> {
  const res = await axios.get(
    `${WORKER_BASE_URL}/api/v1/export`,
    { params: { channel_id: pageId }, headers: getAuthHeaders() },
  );
  return res.data as Record<string, any>;
}

export async function importPageData(
  data: Record<string, any>,
): Promise<{ success: boolean; channel_id: string; stats: Record<string, number> }> {
  const res = await axios.post(
    `${WORKER_BASE_URL}/api/v1/import`,
    data,
    { headers: getAuthHeaders() },
  );
  return res.data as { success: boolean; channel_id: string; stats: Record<string, number> };
}

// ── Zalo QR Login ─────────────────────────────────────────────────────────────
// Worker auto-starts QR login on MQTT browser_account_sync; frontend just polls.
// Use workerApiService methods (zaloGetQRCode / zaloGetLoginStatus / zaloCancelLogin)
// which route via this.client and respect setDirectMode (worker direct URL).
